/**
 * ID3-tag + browser-side audio metadata reader.
 *
 * Reads BPM, Key, Genre, Title, Artist, Duration, embedded cover art and MIME type
 * from an audio File selected/dropped by the admin — no upload required.
 *
 * Returned shape (any field may be null):
 *   { title, artist, bpm, key, genre, duration, fileType, coverBlob, coverMime }
 */
import jsmediatags from 'jsmediatags/dist/jsmediatags.min.js';

const mmSs = (seconds) => {
  if (!seconds || !isFinite(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const detectFileType = (file) => {
  // Map to the store's canonical file_type values used by the release form
  const name = (file.name || '').toLowerCase();
  const mime = (file.type || '').toLowerCase();
  if (name.endsWith('.wav') || mime.includes('wav')) return 'WAV';
  if (name.endsWith('.aiff') || name.endsWith('.aif') || mime.includes('aiff')) return 'AIFF';
  if (name.endsWith('.flac') || mime.includes('flac')) return 'FLAC';
  if (name.endsWith('.mp3') || mime.includes('mp3') || mime.includes('mpeg')) return 'MP3 320';
  return 'MP3 320';
};

const readDuration = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.src = url;
    const done = (d) => {
      URL.revokeObjectURL(url);
      resolve(d);
    };
    audio.onloadedmetadata = () => done(mmSs(audio.duration));
    audio.onerror = () => done(null);
    // Fallback timeout
    setTimeout(() => done(null), 4000);
  });

const readId3 = (file) =>
  new Promise((resolve) => {
    try {
      jsmediatags.read(file, {
        onSuccess: ({ tags }) => resolve(tags || {}),
        onError: () => resolve({}),
      });
    } catch {
      resolve({});
    }
  });

const pickBpm = (tags) => {
  const raw = tags.TBPM?.data ?? tags.bpm ?? tags.BPM;
  if (!raw) return null;
  const n = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? String(n) : null;
};

const pickKey = (tags) => {
  const raw = tags.TKEY?.data ?? tags.key ?? tags.initial_key;
  if (!raw) return null;
  return String(raw).trim().slice(0, 8) || null;
};

const pickGenre = (tags) => {
  const raw = tags.genre ?? tags.TCON?.data;
  if (!raw) return null;
  return String(raw).replace(/^\(\d+\)/, '').trim() || null;
};

const pickCover = (tags) => {
  const pic = tags.picture;
  if (!pic || !pic.data || !pic.data.length) return { blob: null, mime: null };
  const bytes = new Uint8Array(pic.data);
  const mime = pic.format || 'image/jpeg';
  return { blob: new Blob([bytes], { type: mime }), mime };
};

export const readAudioMetadata = async (file) => {
  const [tags, duration] = await Promise.all([readId3(file), readDuration(file)]);
  const { blob: coverBlob, mime: coverMime } = pickCover(tags);
  const rawTitle = tags.title?.trim();
  const cleanTitle =
    rawTitle && rawTitle.length
      ? rawTitle
      : (file.name || '').replace(/\.[^/.]+$/, '').replace(/[_-]+/g, ' ').trim();
  return {
    title: cleanTitle || null,
    artist: tags.artist?.trim() || null,
    bpm: pickBpm(tags),
    key: pickKey(tags),
    genre: pickGenre(tags),
    duration,
    fileType: detectFileType(file),
    coverBlob,
    coverMime,
  };
};

/**
 * Turn a Blob into a File (so it can be posted as multipart form-data with a filename).
 */
export const blobToFile = (blob, filename) => {
  if (!blob) return null;
  return new File([blob], filename, { type: blob.type });
};
